import {AInesAssistantType, AInesFeatureSet} from "@/graphql/generated/graphql";

import {fetchWithAuth} from "@/client";


import OpenAI from "openai";
import {consoleErrorChat} from "@/helper/console";
import {kapitelErrorHandler} from "@/helper/error";
import {AssistantRunConfig} from "@/helper/chat/assistantRun/assistantRunConfig";
import {getOpenAIApiKey, makeMessagePersistParams} from "@/helper/chat/chatBL";
import {query} from "@/graphql/client";
import {gql} from "@urql/vue";
import {KapitelToolCall} from "@/helper/chat/toolCalls";
import {useAuthStore} from "@/store/auth";
import {Stream} from "openai/streaming";
import {v4 as uuidv4} from "uuid";
import {getMetaPromptInfo, getOutputFormatPrompt} from "@/helper/chat/prompt";
import {getFeatureSetsForAssistant} from "@/helper/chat/assistantFeatureMapping";
import {getVersion} from "@/helper/version";
import {addMessageToThread, getMessagesFromThreadStore} from "@/helper/chat/threadStore";
import {ChatCompletionCreateParams} from "openai/resources/chat/completions";
import {APIPromise} from "openai/src/core";
import {ChatCompletion, ChatCompletionChunk} from "openai/src/resources/chat/completions";
import {generateAdditionalInstructions} from "@/helper/chat/additionalInstructions";
import {graphql} from "@/graphql/generated";


export const openaiClient = async() : Promise<OpenAI> => {
    const endpoint = import.meta.env.VITE_AI_ENDPOINT
    let key: string = "";
    const parsedUrl = new URL(endpoint); // Parse the URL
    if(parsedUrl.hostname === "api.openai.com"){
        key = await getOpenAIApiKey()
    }else{
        key = useAuthStore().getToken() ?? ""
    }

    return new OpenAI(
        {
            baseURL: endpoint,
            apiKey: key,
            // apiKey,
            dangerouslyAllowBrowser: true,
            maxRetries: 2,
            timeout: 60*1000
        }
    );
}

export type PromptInfo =  {
    prompt: string,
    model: string,
    tools: [],
    response_format: OpenAI.ResponseFormatText | OpenAI.ResponseFormatJSONObject | OpenAI.ResponseFormatJSONSchema | undefined
}

export type MessagePersistParameters = {
    assistant: string,
    assistantType: AInesAssistantType,
    sessionId: string,
    threadId: string,
    pingPongId: string,
    runId: string|undefined,
    role: "assistant"|"user"|"tool",
    content: string,
    toolCalls: any,
    toolCallId: string|undefined,
    richResponses: any,
    isScriptedContent: boolean,
    isVoiceMode: boolean,
}

const apiErrorHandler = (e: any, kapitelErrorHandlerAlreadyHandled = false) => {
    // log to chat console
    consoleErrorChat(e)

    // handle error (toast, sentry)
    if (!kapitelErrorHandlerAlreadyHandled) {
        kapitelErrorHandler(e)
    }
}

const assistantStore: Map<string, PromptInfo> = new Map<string, PromptInfo>()


export const createAssistant = async (assistantType: AInesAssistantType, assistantFeatureSets: AInesFeatureSet[]): Promise<PromptInfo> => {
    let prompt, model, tools, response_format;
    if(assistantType == AInesAssistantType.Meta){
        return await getMetaPromptInfo()
    }else{
        let response
        try {
            response = await fetchWithAuth(
                import.meta.env.VITE_REST_ENDPOINT + '/aines/get-assistant-prompt-parts',
                JSON.stringify({
                    assistant_feature_sets: assistantFeatureSets
                }),
                'POST',
                {
                    "Content-Type": 'application/json',
                }
            )

        } catch (e: any) {
            apiErrorHandler(e, true)
            throw e
        }

        const promptParts = await response.json()
        return {
            model: promptParts.model,
            prompt: promptParts.basePrompt
                + "\n\n" + promptParts.tonePrompt
                + "\n\n" + getOutputFormatPrompt(promptParts.richResponseData)
                + "\n\n" + promptParts.featurePrompt
                + "\n\n" + promptParts.ragPrompt,
            response_format: promptParts.response_format,
            tools: promptParts.tools
        }
    }
}

export const createAssistantAndStore = async (assistantType: AInesAssistantType): Promise<string> => {
    const newAssistantId = "assistant_" + uuidv4()

    const featureSets = await getFeatureSetsForAssistant(assistantType)
    const promptInfo = await createAssistant(assistantType, featureSets)

    assistantStore.set(newAssistantId, promptInfo)

    return newAssistantId

};


export const getAssistantFromStore = (runConfig: AssistantRunConfig): PromptInfo => {
    if(!assistantStore.has(runConfig.assistantId)){
        throw new Error("Assistant not found: " + runConfig.assistantId)
    }

    return assistantStore.get(runConfig.assistantId) as PromptInfo
}
export const  generateAssistantDeveloperMessage = async (runConfig: AssistantRunConfig) => {
    const promptInfo = getAssistantFromStore(runConfig)
    return {
        role: 'developer',
        content: promptInfo.prompt + '\n\n' + await generateAdditionalInstructions(runConfig)
    } as OpenAI.ChatCompletionDeveloperMessageParam
}




export const performToolCallRequest = async (toolCall: KapitelToolCall): Promise<any> => {
    try {
        const param = JSON.stringify({
            id: toolCall.id,
            type: 'function',
            function: {
                name: toolCall.function,
                arguments: toolCall.arguments,
            }
        })

        const response = await fetchWithAuth(
            import.meta.env.VITE_REST_ENDPOINT + '/aines/perform-tool-call',
            param,
            "POST"
        );
        if (!response?.body) {
            throw Error("No valid response given from perform-toolcall for: " + param)
        }

        return await response.text()
    } catch (e: any) {
        apiErrorHandler(e, true)
        throw e
    }
}


export async function runCompletionStream(messageCreateParams: ChatCompletionCreateParams) {
    try {
        return (await openaiClient()).chat.completions.create({
            ...messageCreateParams,
            stream: true
        })
    } catch (e: any) {
        apiErrorHandler(e, false)
        throw e
    }
}
export async function runCompletion(messageCreateParams: ChatCompletionCreateParams) {
    try {
        return (await openaiClient()).chat.completions.create({
            ...messageCreateParams,
            stream: false
        },)
    } catch (e: any) {
        apiErrorHandler(e, false)
        throw e
    }
}


export const tts = async (payload: string): Promise<any> => {
    try {
        // Prepare the request payload
        const requestPayload = {
            model: import.meta.env.VITE_AUDIO_MODEL,
            modalities: ['text', 'audio'],
            audio: {
                voice: 'shimmer',
                format: 'pcm16'
            },
            messages: [
                {
                    role: 'user',
                    content: 'Sage folgenden Text: "'+payload+'"',
                }
            ],
            stream: true
        };

        // Send the request to the OpenAI Chat API
        return (await openaiClient()).chat.completions.create(requestPayload as {model: any, modalities: any, audio: any, messages: any});
    } catch (error) {
        console.error('Error generating audio response:', error);
        throw error;
    }
}


/**
 * wraps base64 in file and calls openai API via transcribeAudioFile()
 */
export const transcribeAudioBase64 = async (b64Audio: string, mimeType: string): Promise<string | undefined> => {

    const base64ToBlob = (base64: string, mime: string): Blob => {
        const byteChars = atob(base64);
        const byteArrays = [];

        for (let offset = 0; offset < byteChars.length; offset += 512) {
            const slice = byteChars.slice(offset, offset + 512);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, {type: mime});
    }

    let filename = "audio.webm"

    if (mimeType === 'audio/mp4') {
        filename = "audio.mp4"
        alert(mimeType + ' unsupported by OpenAI API - use Chrome browser or native app')
        return;
    } else if (mimeType === 'audio/aac') {
        filename = "audio.m4a"
        alert(mimeType + ' unsupported by OpenAI API - use Chrome browser or native app')
        return;
    }

    const audioFile = new File([base64ToBlob(b64Audio, mimeType)], filename, {type: mimeType});

    return await transcribeAudioFile(audioFile)
}

/**
 * helper list
 */
const transcriptionIgnoreList = [
    "Untertitelung aufgrund der Amara.org-Community",
    "Untertitel im Auftrag des ZDF für funk, 2017",
    "Untertitel von Stephanie Geiges",
    "Untertitel der Amara.org-Community",
    "Untertitel im Auftrag des ZDF, 2017",
    "Untertitel im Auftrag des ZDF, 2020",
    "Untertitel im Auftrag des ZDF, 2018",
    "Untertitel im Auftrag des ZDF, 2021",
    "Untertitelung im Auftrag des ZDF, 2021",
    "Copyright WDR 2021",
    "Copyright WDR 2020",
    "Copyright WDR 2019",
    "SWR 2021",
    "SWR 2020",
    "Vielen Dank für's Zuschauen!",
    "Bis zum nächsten Mal."
]

/**
 * helper function
 */

/**
 *
 */
const transcribeAudioFile = async (file: File): Promise<string | undefined> => {
    const formData = new FormData();
    formData.append("file", file);

    const maxNumberOfTries: number = 3
    let currentNumberOfTries: number = 0
    let transcriptionText = undefined;
    while (!transcriptionText  && currentNumberOfTries<maxNumberOfTries){
        try {
            const transcription = await fetchWithAuth(
                import.meta.env.VITE_REST_ENDPOINT + '/aines/transcription',
                formData,
                "POST"
            )

            const transcriptionTextObject = await transcription.text();
            transcriptionText = (await JSON.parse(transcriptionTextObject))?.text || '';
        } catch (e: any) {
            console.log("transcription failed " + (currentNumberOfTries+1).toString() + " times.")
            transcriptionText = undefined
            apiErrorHandler(e, true)
            throw e
        }
        currentNumberOfTries++;
    }
    if(!transcriptionText){
        console.log("transcription failed.")
        return undefined
    }

    // mostly on empty audio translations we get strange responses - this fixes those cases
    const removeIgnoredTranscriptions = (transcription: string) => {
        transcriptionIgnoreList.forEach(ignoreString => {
            transcription = transcription.replace(new RegExp(ignoreString, 'g'), '');
        });
        return transcription;
    };

    // Example usage:
    transcriptionText = removeIgnoredTranscriptions(transcriptionText).trim();

    return transcriptionText
};

export const persistMessage = async (
   params: MessagePersistParameters
): Promise<any> => {
    const response = await fetchWithAuth(
        import.meta.env.VITE_REST_ENDPOINT + '/aines/persist-message' + '?' + params.assistantType,
        JSON.stringify({
            assistant: params.assistant,
            assistantType: params.assistantType,
            sessionId: params.sessionId,
            threadId: params.threadId,
            pingPongId: params.pingPongId,
            runId: params.runId,
            role: params.role,
            content: params.content,
            toolCalls: params.toolCalls,
            toolCallId: params.toolCallId,
            richResponses: params.richResponses,
            isScriptedContent: params.isScriptedContent,
            isVoiceMode: params.isVoiceMode
        }),
        "POST"
    );
    if (!response?.body) {
        throw Error("No valid response given.")
    }
    return await response.json()
}

export const getApiKey = async () => {
    const result = await query(graphql(`
        query GetOpenaiApiKey{
            openaiApiKey 
        }`)
    )

    return result?.data.openaiApiKey
};
